import { Popup } from '@picsart/design-system/components/Popup';
import { InlineButton } from '@picsart/design-system/components/InlineButton';
import { IconCross } from '@picsart/design-system/foundation/icons/IconCross';
import { useTranslator } from '@picsart/rc/services/localization';
import useStyles from './styles';
import type { CanvasImage } from '@/utils/canvas/canvasUtils';
import type { CanvasImageSelectorProps } from './types';

/**
 * Modal component for selecting an image from the canvas/editor
 * Shows a grid of available canvas images for single selection
 */
const CanvasImageSelector = ({
  isOpen,
  images,
  onSelect,
  onClose,
}: CanvasImageSelectorProps) => {
  const classes = useStyles();
  const translate = useTranslator();

  const handleImageSelect = (image: CanvasImage) => {
    onSelect(image);
    onClose();
  };

  return (
    <Popup
      isShown={isOpen}
      onClose={onClose}
      coverage="global"
      isWithOutSideClick
      ariaLabel="Select from Canvas"
    >
      <div
        className={classes.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="canvas-selector-title"
      >
        <div className={classes.header}>
          <h2 id="canvas-selector-title" className={classes.title}>
            {translate('Select from Canvas')}
          </h2>
          <InlineButton
            size="large"
            centerIcon={IconCross}
            isWithoutBorder
            isWithoutBackground
            onClick={onClose}
            aria-label="Close"
            dataTestId="canvas-image-selector-close"
          />
        </div>

        <div className={classes.content}>
          {images.length === 0 ? (
            <div className={classes.emptyState}>
              {translate('No images found on canvas')}
            </div>
          ) : (
            <div
              className={`${classes.grid} ${
                images.length === 1 ? classes.gridSingle : classes.gridMultiple
              }`}
            >
              {images.map((image) => (
                <button
                  key={image.id}
                  type="button"
                  className={classes.imageItem}
                  onClick={() => handleImageSelect(image)}
                  aria-label={`Select ${image.name}`}
                  data-testid={`canvas-image-${image.id}`}
                >
                  <img
                    src={image.url}
                    alt={image.name}
                    className={classes.image}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </Popup>
  );
};

export default CanvasImageSelector;
